import BookmarkSvg from '@/assets/icons/bookmark.svg?react'
import chatMessagesSvg from '@/assets/icons/chat-messages.svg'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import {useCurrencySymbol} from '@/lib/locale/country'
import {useLocale} from '@/lib/locale/locale'
import createProductQueryOptions from '@/lib/product/utils/createProductQueryOptions'
import type {Product} from '@/models/productModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useIdleTimeScheduler from '@/utils/hooks/useIdleTimeScheduler'
import roundToNDecimal from '@/utils/numbers/roundToNDecimals'

import ProductBookmarker from './ProductBookmarker'
import style from './ProductCard.module.scss'
import ProductCardActions from './ProductCardActions'
import ProductOriginalPrice from './ProductOriginalPrice'
import ProductPrice from './ProductPrice'
import ProductProgramImage from './ProductProgramImage'
import ProductRating from './ProductRating'
import ProductRetailerImage from './ProductRetailerImage'
import ProductShippingInfo from './ProductShippingInfo'

const PriceHistory = lazy(
  async () => import('@/components/ChatSection/componnents/PriceHistory/PriceHistory'),
)

interface BaseProps {
  product: Product
  headingLevel: number
  className?: string
}

interface Props extends BaseProps {
  isInMessage?: false
}

interface PropsInMessage extends BaseProps {
  isInMessage: true
  submitMessage: (message: string) => void
  deals?: false
}

export default deepMemo(function ProductCard(props: Props | PropsInMessage) {
  const scheduler = useIdleTimeScheduler()
  const currencySymbol = useCurrencySymbol()
  const [locale] = useLocale()

  let product = props.product

  // NOTE: cause error
  const {data: productDetailsResult, refetch} = useQuery(
    createProductQueryOptions(locale, product, {
      enabled: false,
    }),
  )

  if (productDetailsResult && productDetailsResult.products[0]) {
    product = productDetailsResult.products[0]
  }

  useEffect(
    function loadDetailsIfPriceIsNotDeterminedYet() {
      if (typeof product.price === 'string') {
        scheduler.schedule(() => {
          void refetch()
        })
      }
    },
    [refetch, product.price],
  )

  const priceChangePercentage =
    product.price &&
    product.orgPrice &&
    typeof product.price === 'number' &&
    typeof product.orgPrice === 'number'
      ? roundToNDecimal((1 - product.price / product.orgPrice) * 100, 0)
      : 0

  const priceChangeAmount =
    product.orgPrice &&
    typeof product.orgPrice === 'number' &&
    product.price &&
    typeof product.price === 'number'
      ? roundToNDecimal(product.orgPrice - product.price)
      : 0

  const shouldDisplayDealInfo =
    !!product.orgPrice &&
    !!priceChangePercentage &&
    (priceChangePercentage >= 20 || priceChangeAmount >= 20)

  const dealInfo = (() => {
    if (priceChangePercentage >= 20 && priceChangeAmount < 20) return `${priceChangePercentage}%`
    else if (priceChangePercentage < 20 && priceChangeAmount >= 20)
      return currencySymbol + priceChangeAmount.toFixed(USD_DISPLAY_DECIMALS)
    return `${priceChangePercentage}%`
  })()

  // const schedulePreload = useCallback(() => {
  //   return scheduler.schedule(() => {
  //     void preloadProduct(product, locale, queryClient)
  //   })
  // }, [locale, product, queryClient])

  // const interactedActionProps = useInteractedActionProps(schedulePreload)

  return (
    <div
      className={clsx(style.ProductWrapper, props.className)}
      // {...interactedActionProps}
    >
      <div className={style.Product}>
        <div className={style.ImageWrapper}>
          <Link
            className={style.ImageWrapper}
            to={'/product/$productId'}
            params={{
              productId: {
                retailer: product.retailer,
                id: product.id,
              },
            }}
          >
            <VisuallyHidden as='div' role='heading' aria-level={props.headingLevel}>
              {product.title}
            </VisuallyHidden>
            <img
              className={style.ProductImage}
              src={product.image}
              alt=''
              loading='lazy'
              referrerPolicy='no-referrer'
            />
          </Link>
          <div className={style.ShippingInfoContainer}>
            <ProductShippingInfo value={product.shipping} />
          </div>
          {shouldDisplayDealInfo && (
            <div className={style.Deal}>
              <VisuallyHidden>Discount: </VisuallyHidden> {`${dealInfo} OFF`}
              <span aria-hidden='true'> 🔥</span>
            </div>
          )}
        </div>
        <div className={style.ProductBottom}>
          <div className={style.ProductFrom}>
            <ProductRetailerImage retailer={product.retailer} seller={product.seller} />
            <ProductProgramImage program={product.program} />
          </div>
          <div className={style.ProductInfo}>
            <Link
              to={'/product/$productId'}
              params={{
                productId: {
                  retailer: product.retailer,
                  id: product.id,
                },
              }}
              className={style.ProductName}
              rel='noreferrer'
              tabIndex={-1}
              aria-hidden='true'
            >
              {product.title}
            </Link>
            {/* <div className={style.ProductDescription}>Description</div> */}
            {product.rating ? (
              <ProductRating rating={product.rating} ratingCount={product.ratingCount} />
            ) : null}
            <div className={style.Divider}></div>
            <div className={style.Prices}>
              <ProductPrice price={product.price} />
              <Suspense>
                <PriceHistory id={product.id} title={product.title} retailer={product.retailer} />
              </Suspense>
            </div>
            {!!product.orgPrice && (
              <ProductOriginalPrice orgPrice={product.orgPrice} price={product.price} />
            )}
            {!!product.return && <div className={style.ReturnPolicy}>{product.return}</div>}
          </div>
          {props.isInMessage && (
            <div className={style.ProductActions}>
              <button
                aria-label={`Chat with Innosearch for ${product.title}`}
                onClick={() => {
                  props.submitMessage(`Research on ${product.title}`)
                }}
              >
                <img src={chatMessagesSvg} alt='' />
                Learn more
              </button>
              {props.deals !== false && (
                <button
                  aria-label={`Find deals on ${product.title}`}
                  onClick={() => {
                    props.submitMessage(`Find deals on ${product.title}`)
                  }}
                >
                  Deals
                </button>
              )}
            </div>
          )}
          <ProductCardActions product={product} />
        </div>
        <ProductBookmarker
          className={style.Bookmark}
          activeClassName={style.isActive}
          product={product}
        >
          <BookmarkSvg aria-hidden />
        </ProductBookmarker>
      </div>
    </div>
  )
}, 'all')
